import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TestCard from './components/TestCard';
import TestDetailModal from './components/TestDetailModal';
import FilterPanel from './components/FilterPanel';
import StatsCards from './components/StatsCards';
import CreateTestModal from './components/CreateTestModal';
import { abTestingService } from '../../services/abTestingService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import FeatureGate from '../../components/plan/FeatureGate';

const ABTestingDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, paused: 0 });
  
  const [filters, setFilters] = useState({
    status: null,
    searchTerm: ''
  });
  
  const [selectedTest, setSelectedTest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/authentication');
      return;
    }

    loadData();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;

    // Subscribe to real-time updates
    const testSubscription = supabase
      ?.channel('ab_tests_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ab_tests'
        },
        () => {
          loadData();
        }
      )
      ?.subscribe();

    return () => {
      testSubscription?.unsubscribe();
    };
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [testsData, statsData] = await Promise.all([
        abTestingService?.getAllTests(),
        abTestingService?.getTestStats()
      ]);
      
      setTests(testsData || []);
      setStats(statsData || { total: 0, active: 0, completed: 0, paused: 0 });
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load A/B tests');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTest = async (test) => {
    try {
      const fullTest = await abTestingService?.getTestById(test?.id);
      setSelectedTest(fullTest);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error loading test details:', err);
      setError(err?.message || 'Failed to load test details');
    }
  };

  const handlePauseTest = async (test) => {
    try {
      await abTestingService?.updateTest(test?.id, {
        status: test?.status === 'paused' ? 'active' : 'paused'
      });
      await loadData();
    } catch (err) {
      console.error('Error updating test:', err);
      setError(err?.message || 'Failed to update test');
    }
  };

  const handleCompleteTest = async (test) => {
    if (!confirm(`Mark "${test?.testName}" as completed?`)) return;

    try {
      await abTestingService?.updateTest(test?.id, {
        status: 'completed',
        endDate: new Date()?.toISOString()
      });
      await loadData();
    } catch (err) {
      console.error('Error completing test:', err);
      setError(err?.message || 'Failed to complete test');
    }
  };

  const handleDeleteTest = async (test) => {
    if (!confirm(`Are you sure you want to delete "${test?.testName}"?`)) return;

    try {
      await abTestingService?.deleteTest(test?.id);
      await loadData();
    } catch (err) {
      console.error('Error deleting test:', err);
      setError(err?.message || 'Failed to delete test');
    }
  };

  const filteredTests = tests?.filter(test => {
    if (filters?.status && test?.status !== filters?.status) return false;
    if (filters?.searchTerm && !test?.testName?.toLowerCase()?.includes(filters?.searchTerm?.toLowerCase())) return false;
    return true;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar>
          <div className="flex items-center justify-center py-20">
            <Icon name="Loader2" size={40} className="animate-spin text-primary" />
          </div>
        </NavigationBar>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar>
      <FeatureGate feature="ab_testing">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">A/B Testing Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Send visitors to two forms from one link, then compare real conversion results.
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Icon name="Plus" size={18} />
            Create A/B test
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <Icon name="AlertCircle" size={20} className="text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <StatsCards stats={stats} />

        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          onFiltersChange={setFilters}
        />

        {/* Tests Grid */}
        {filteredTests?.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <Icon name="FlaskConical" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {tests?.length === 0 ? 'No A/B tests yet' : 'No tests match your filters'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {tests?.length === 0
                ? 'Compare two published forms, choose the traffic split, and share one experiment link.'
                : 'Try adjusting your filters to see more results'}
            </p>
            {tests?.length === 0 && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Icon name="Plus" size={18} />
                Create your first test
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTests?.map(test => (
              <TestCard
                key={test?.id}
                test={test}
                onView={handleViewTest}
                onPause={handlePauseTest}
                onComplete={handleCompleteTest}
                onDelete={handleDeleteTest}
              />
            ))}
          </div>
        )}
      </div>

      {showDetailModal && selectedTest && (
        <TestDetailModal
          test={selectedTest}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTest(null);
          }}
          onUpdate={loadData}
        />
      )}
      {showCreateModal && (
        <CreateTestModal
          onClose={() => setShowCreateModal(false)}
          onCreated={async () => {
            setShowCreateModal(false);
            await loadData();
          }}
        />
      )}
      </FeatureGate>
    </NavigationBar>
    </div>
  );
};

export default ABTestingDashboard;